"use client";

import { useRef } from "react";
import type { ActivitySessionFeedItem } from "@/actions/activity-session";
import { ActivitySessionCard } from "@/components/feed/activity-session-card";
import type { ActivityDefinition } from "@/lib/activities";
import { deriveActivitySessionCreatePricing } from "@/lib/activity-sessions/session-create";
import type { ActivitySessionValues } from "@/validations/activity-session";

type SessionPreviewCardProps = {
  values: ActivitySessionValues;
  activity: ActivityDefinition | null;
  title: string;
  creator: {
    id?: string;
    displayName: string;
    avatarUrl?: string | null;
  };
};

const toDate = (date: string, time: string, fallback: Date): Date => {
  if (!date || !time) {
    return fallback;
  }
  const parsed = new Date(`${date}T${time}`);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

/**
 * Live preview of how a session will appear in the feed. Builds a synthetic
 * feed item from the in-progress form and renders the REAL ActivitySessionCard
 * (incl. the court preview, badges, glass cards) wrapped non-interactive — so
 * the preview is an exact match for the published card.
 */
export function SessionPreviewCard({
  values,
  activity,
  title,
  creator,
}: SessionPreviewCardProps) {
  // Stable across re-renders so the preview's "createdAt" doesn't churn.
  const now = useRef(new Date()).current;
  const startTime = toDate(values.startDate, values.startTime, now);
  const endTime = toDate(
    values.startDate,
    values.endTime,
    new Date(startTime.getTime() + 60 * 60 * 1000),
  );
  const pricing = deriveActivitySessionCreatePricing(values);
  const organizerPlaying = values.collectMoney ? values.organizerPlaying : true;
  const creatorId = creator.id ?? "preview";

  const event: ActivitySessionFeedItem = {
    id: 0,
    activityId: values.activityId,
    activityName: activity?.name ?? "Activity",
    activityIcon: activity?.iconKey ?? "",
    title: title || `${activity?.name ?? "Activity"} session`,
    description: values.description ?? "",
    entryType: "session",
    creator: {
      id: creatorId,
      displayName: creator.displayName,
      username: null,
      avatarUrl: creator.avatarUrl ?? null,
      isBlocked: false,
    },
    visibility: values.visibility,
    status: "upcoming",
    cancelReason: null,
    scoreVerificationStatus: null,
    scoreSummary: null,
    competitionMode: values.competitionMode ?? null,
    sessionFormat: values.sessionFormat ?? null,
    requiresApproval: values.requiresApproval ?? false,
    startTime,
    endTime,
    locationName: values.locationName ?? "",
    address: values.address ?? "",
    latitude: values.latitude ?? 0,
    longitude: values.longitude ?? 0,
    pricingType: pricing.pricingType,
    pricePerPersonAmountMinor: pricing.pricePerPersonAmountMinor,
    splitPricing: values.collectMoney ? values.splitPricing : null,
    currency: values.currency,
    minSkillLevel: values.minSkillLevel ?? null,
    maxSkillLevel: values.maxSkillLevel ?? null,
    viewerSkillLevel: null,
    isViewerSkillLevelEligible: null,
    scoringSchema: null,
    minParticipants: values.minParticipants,
    maxParticipants: values.maxParticipants,
    participantCount: organizerPlaying ? 1 : 0,
    participants: organizerPlaying
      ? [
          {
            id: creatorId,
            displayName: creator.displayName,
            username: null,
            avatarUrl: creator.avatarUrl ?? null,
            role: "creator",
            team: values.sessionFormat === "team" ? "team1" : null,
            isBlocked: false,
          },
        ]
      : [],
    isJoinedByViewer: true,
    isCreatedByViewer: true,
    viewerJoinRequest: null,
    likeCount: 0,
    commentCount: 0,
    shareCount: 0,
    isLikedByViewer: false,
    createdAt: now,
  };

  return (
    <div
      className="pointer-events-none overflow-hidden rounded-2xl border border-border"
      aria-hidden="true"
    >
      <ActivitySessionCard event={event} />
    </div>
  );
}
